import {
  cn,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@nexus/ui";

import { TASK_PROGRESS_PANEL_ID } from "@/features/task-progress/store/task-sidebar.store";

import { PersonaOpportunityInsight } from "../../cards/PersonaOpportunityCard/PersonaOpportunityInsight.js";
import { PersonaOpportunitySupportingMedia } from "../../shared/PersonaOpportunitySupportingMedia/PersonaOpportunitySupportingMedia.js";

import type { HubChromeOpportunityRow } from "./hub-chrome.types.js";
import { drivingIdentifier } from "./hub-opportunity-columns.js";
import { OpportunityHubCompetitorDetails } from "./OpportunityHubCompetitorDetails.js";
import {
  OpportunityHubPostFlightDetails,
  type PostFlightDrawerScript,
} from "./OpportunityHubPostFlightDetails.js";

/** Marks the hub opportunities table so opening another row does not dismiss the drawer. */
export const OPPORTUNITY_HUB_TABLE_SELECTOR = "[data-opportunity-hub-table]";

/** Marks the hub scripts list so opening another document does not dismiss the drawer. */
export const OPPORTUNITY_HUB_SCRIPTS_SELECTOR = "[data-opportunity-hub-scripts]";

/**
 * Docks hub drawers under TopNav (h-14) and below the task-progress panel (z-30),
 * so the Working pill and auto-expanded task cards stay visible over an open
 * drawer. Hub page content tops out at z-10.
 */
export const HUB_DRAWER_DOCKED_CLASS = "top-14 h-auto z-20";

// Non-modal sheet: clicks in the table swap the open opportunity, and nav menus
// and the task panel portal outside the sheet. Keep the drawer open for those,
// matching the legacy opportunities drawer.
const KEEP_HUB_DRAWER_OPEN_SELECTORS = [
  OPPORTUNITY_HUB_TABLE_SELECTOR,
  OPPORTUNITY_HUB_SCRIPTS_SELECTOR,
  "[data-top-nav]",
  "[role='menu']",
  "[data-radix-popper-content-wrapper]",
  `#${TASK_PROGRESS_PANEL_ID}`,
];

export function shouldKeepHubDrawerOpen(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return KEEP_HUB_DRAWER_OPEN_SELECTORS.some((selector) => target.closest(selector) !== null);
}

/** Persona angles get the insight body. Custom rows have no generated content to show. */
export function showsPersonaOpportunityDrawerBody(row: HubChromeOpportunityRow): boolean {
  return row.setType === "PERSONA" && !row.isCustom;
}

export function showsCompetitorOpportunityDrawerBody(row: HubChromeOpportunityRow): boolean {
  return row.setType === "COMPETITOR" && !row.isCustom;
}

export function showsPostFlightOpportunityDrawerBody(row: HubChromeOpportunityRow): boolean {
  return row.setType === "POST_FLIGHT";
}

export interface OpportunityHubOpportunityDrawerProps {
  opportunity: HubChromeOpportunityRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scripts?: PostFlightDrawerScript[];
  onOpenScript?: (scriptId: string) => void;
}

export function OpportunityHubOpportunityDrawer({
  opportunity,
  open,
  onOpenChange,
  scripts,
  onOpenScript,
}: OpportunityHubOpportunityDrawerProps) {
  const showPersonaBody = opportunity != null && showsPersonaOpportunityDrawerBody(opportunity);
  const showPostFlightBody = opportunity != null && showsPostFlightOpportunityDrawerBody(opportunity);

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        closePosition="left"
        closeIcon="chevron-right"
        className={cn(
          "flex w-full flex-col overflow-y-auto bg-card p-0 sm:max-w-[1100px] 2xl:max-w-[1300px]",
          HUB_DRAWER_DOCKED_CLASS,
        )}
        onPointerDownOutside={(event) => {
          if (shouldKeepHubDrawerOpen(event.target)) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (shouldKeepHubDrawerOpen(event.target)) event.preventDefault();
        }}
      >
        {opportunity && showsCompetitorOpportunityDrawerBody(opportunity) ? (
          <OpportunityHubCompetitorDetails row={opportunity} />
        ) : showPostFlightBody && opportunity ? (
          <OpportunityHubPostFlightDetails row={opportunity} scripts={scripts} onOpenScript={onOpenScript} />
        ) : opportunity ? (
          <>
            <SheetHeader className="border-b px-6 pb-5 pt-6">
              <div className="flex flex-col gap-1.5 pl-10">
                <SheetTitle className="break-words text-base leading-snug">
                  {drivingIdentifier(opportunity)}
                </SheetTitle>
                <SheetDescription className="sr-only">{opportunity.setName}</SheetDescription>
              </div>
            </SheetHeader>
            {showPersonaBody ? (
              <SheetBody className="flex flex-col gap-6 sm:flex-row sm:items-start">
                <PersonaOpportunitySupportingMedia row={opportunity} variant="drawer" />
                <PersonaOpportunityInsight row={opportunity} />
              </SheetBody>
            ) : null}
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
